import React from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import BreadCrumbs from '@lib/components/BreadCrumbs/BreadCrumbs';
import PageBody from '@lib/layouts/DashboardLayout/PageBody/PageBody';
import TabBar from '@lib/components/TabBar/TabBar';
import { TabBarItem } from '@lib/components/TabBar/constants';
import { getBreadCrumbsDataByKey } from 'utils/breadcrumbs';
import { APP_URLS } from 'constants/urls';
import DepartmentFormPage from './DepartmentFormPage/DepartmentFormPage';
import DepartmentViewDetails from './DepartmentViewDetails/DepartmentViewDetails';
import DepartmentViewMembers from './DepartmentViewMembers/DepartmentViewMembers';
import DepartmentViewAccessGroups from './DepartmentViewAccessGroups/DepartmentViewAccessGroups';
import DepartmentViewTasks from './DepartmentViewTasks/DepartmentViewTasks';
import DepartmentViewInterventions from './DepartmentViewInterventions/DepartmentViewInterventions';
import useDepartmentQueries from './utils/useDepartmentQueries';
import { DepartmentViewMode } from './enums';

type Props = {
  viewMode: DepartmentViewMode;
};

export default function DepartmentContainer({ viewMode }: Props) {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const { id = '' } = useParams();
  const navigate = useNavigate();

  const useDepartmentQueriesResult = useDepartmentQueries({ viewMode });
  const { useDepartmentQueryHookResult } = useDepartmentQueriesResult;

  const isForm =
    viewMode === DepartmentViewMode.Add || viewMode === DepartmentViewMode.Edit;

  const tabs = [
    { label: t('department-details'), value: DepartmentViewMode.ViewDetails },
    { label: t('members'), value: DepartmentViewMode.ViewMembers },
    { label: t('access-groups'), value: DepartmentViewMode.ViewAccessGroups },
    { label: t('linked-tasks'), value: DepartmentViewMode.ViewTasks },
    {
      label: t('linked-interventions'),
      value: DepartmentViewMode.ViewInterventions,
    },
  ];

  const onViewChange = (newViewMode: DepartmentViewMode) => {
    const pathParts = { id };
    switch (newViewMode) {
      case DepartmentViewMode.ViewMembers:
        navigate(
          APP_URLS.dashboard.departments.members.getDynamicPath({ pathParts }),
        );
        break;
      case DepartmentViewMode.ViewAccessGroups:
        navigate(
          APP_URLS.dashboard.departments.accessGroups.getDynamicPath({
            pathParts,
          }),
        );
        break;
      case DepartmentViewMode.ViewTasks:
        navigate(
          APP_URLS.dashboard.departments.tasks.getDynamicPath({ pathParts }),
        );
        break;
      case DepartmentViewMode.ViewInterventions:
        navigate(
          APP_URLS.dashboard.departments.interventions.getDynamicPath({
            pathParts,
          }),
        );
        break;
      default:
        navigate(
          APP_URLS.dashboard.departments.view.getDynamicPath({ pathParts }),
        );
    }
  };

  return (
    <>
      <BreadCrumbs
        data={getBreadCrumbsDataByKey({
          pathname,
          t,
          entity: useDepartmentQueryHookResult.data || { id },
        })}
      />
      {isForm && (
        <DepartmentFormPage
          viewMode={viewMode}
          useDepartmentQueriesResult={useDepartmentQueriesResult}
        />
      )}
      {!isForm && (
        <PageBody withoutHeader>
          <TabBar
            items={tabs}
            active={viewMode}
            onActiveChange={({ value }: TabBarItem) =>
              onViewChange(value as DepartmentViewMode)
            }
          />
          {viewMode === DepartmentViewMode.ViewDetails && (
            <DepartmentViewDetails
              useDepartmentQueriesResult={useDepartmentQueriesResult}
            />
          )}
          {viewMode === DepartmentViewMode.ViewMembers && (
            <DepartmentViewMembers
              useDepartmentQueriesResult={useDepartmentQueriesResult}
            />
          )}
          {viewMode === DepartmentViewMode.ViewAccessGroups && (
            <DepartmentViewAccessGroups
              useDepartmentQueriesResult={useDepartmentQueriesResult}
            />
          )}
          {viewMode === DepartmentViewMode.ViewTasks && (
            <DepartmentViewTasks
              useDepartmentQueriesResult={useDepartmentQueriesResult}
            />
          )}
          {viewMode === DepartmentViewMode.ViewInterventions && (
            <DepartmentViewInterventions
              useDepartmentQueriesResult={useDepartmentQueriesResult}
            />
          )}
        </PageBody>
      )}
    </>
  );
}
